import { FC, Suspense, useEffect } from 'react';
import { RouterProvider } from 'react-router-dom';
import router from '@routing/routes';
import { ToastContainer } from 'react-toastify';
import { FadeInDown } from '@utils/toastAnimations';
import useAxiosInterceptor from '@hooks/useAxiosInterceptor';
import { useMsal } from '@azure/msal-react';
import { useAtom } from 'jotai';
import { redirectAfterLoginAtom } from '@js/store/authenticationAtoms';
import { AuthenticationResult, EventMessage, EventPayload, EventType } from '@azure/msal-browser';

const Root: FC = () => {
    const interceptorsReady = useAxiosInterceptor();
    const [redirectAfterLogin, setRedirectAfterLogin] = useAtom(redirectAfterLoginAtom);
    const { instance } = useMsal();

    const isAuthenticationResult = (payload: EventPayload): payload is AuthenticationResult => {
        return typeof payload === 'object' && payload !== null && 'account' in payload;
    };

    useEffect(() => {
        const setMsalAccount = async () => {
            await instance.initialize();

            // Set active account on login success (redirect or popup)
            instance.addEventCallback((event: EventMessage) => {
                if (event.eventType === EventType.LOGIN_SUCCESS && isAuthenticationResult(event.payload)) {
                    const account = event.payload.account;
                    instance.setActiveAccount(account);
                }
            });

            instance.handleRedirectPromise().then((response) => {
                if (response?.state && !redirectAfterLogin) {
                    setRedirectAfterLogin((oldState) => {
                        // If we already have a from, we don't want to overwrite it
                        if (oldState) {
                            return oldState;
                        }

                        return response.state;
                    });
                }

                if (!response?.account) {
                    return;
                }

                // Set active account on redirect
                instance.setActiveAccount(response.account);
            });
        };

        setMsalAccount();
    }, []);

    if (!interceptorsReady) {
        return null;
    }

    return (
        <Suspense>
            <RouterProvider router={router} />
            <ToastContainer position="bottom-right" autoClose={3000} transition={FadeInDown} theme="dark" />
        </Suspense>
    );
};

export default Root;
